import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  HelpCircle,
  Maximize,
  Minimize,
  Pause,
  Play,
  RefreshCw,
  Server,
  HardDrive,
  TrendingUp,
  Activity,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  success: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-500/20", border: "border-l-emerald-500", label: "OK" },
  warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-500/20", border: "border-l-amber-500", label: "Attention" },
  error: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/20", border: "border-l-red-500", label: "Erreur" },
  running: { icon: Loader2, color: "text-blue-500", bg: "bg-blue-500/20", border: "border-l-blue-500", label: "En cours" },
  unknown: { icon: HelpCircle, color: "text-slate-500", bg: "bg-slate-500/20", border: "border-l-slate-500", label: "Inconnu" },
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "-";
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const formatDuration = (seconds) => {
  if (!seconds) return "-";
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h${Math.floor((seconds % 3600) / 60)}m`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m${seconds % 60}s`;
  return `${seconds}s`;
};

const MonitoringByCode = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [expandedServer, setExpandedServer] = useState(null);
  const containerRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/monitoring/code/${code}`);
      if (!res.ok) throw { status: res.status };
      const json = await res.json();
      setData(json);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      if (err.status === 404) {
        setError("Code de monitoring invalide");
      } else {
        setError("Erreur lors du chargement des donnees");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [code]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-500" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center max-w-md">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">{error}</h1>
          <p className="text-slate-400">Verifiez le code et reessayez.</p>
        </div>
      </div>
    );
  }

  const sortedServers = [...(data?.servers || [])].sort((a, b) => {
    const order = { error: 0, warning: 1, running: 2, unknown: 3, success: 4 };
    return (order[a.last_backup_status] || 4) - (order[b.last_backup_status] || 4);
  });

  const stats = data?.stats || {};
  const trends = data?.trends || [];
  const maxTrend = Math.max(...trends.map((t) => t.total_backups || 0), 1);

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0B1120] text-white" data-testid="monitoring-by-code-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0B1120]/95 backdrop-blur-sm border-b border-slate-800 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Shield className="text-white" size={22} />
            </div>
            <div>
              <h1 className="font-bold text-lg text-white">{data?.client?.name}</h1>
              <p className="text-xs text-slate-500">ServeurOnline Monitoring</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Status pills */}
            <div className="hidden sm:flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1.5 bg-emerald-500/10 text-emerald-400 px-2.5 py-1 rounded-full font-mono text-xs">
                <CheckCircle size={12} /> {stats.success || 0}
              </span>
              <span className="flex items-center gap-1.5 bg-amber-500/10 text-amber-400 px-2.5 py-1 rounded-full font-mono text-xs">
                <AlertTriangle size={12} /> {stats.warning || 0}
              </span>
              <span className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-2.5 py-1 rounded-full font-mono text-xs">
                <XCircle size={12} /> {stats.error || 0}
              </span>
            </div>

            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock size={12} />
              {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: fr })}
            </span>

            <div className="flex items-center gap-1.5">
              <button onClick={fetchData} className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" data-testid="refresh-btn">
                <RefreshCw size={16} />
              </button>
              <button onClick={() => setIsPaused(!isPaused)} className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
                {isPaused ? <Play size={16} /> : <Pause size={16} />}
              </button>
              <button onClick={toggleFullscreen} className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
                {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-row">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Serveurs</p>
            <p className="text-3xl font-bold text-white">{stats.total_servers || 0}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Taux de succes 30j</p>
            <p className={`text-3xl font-bold ${data?.success_rate_30d >= 90 ? "text-emerald-400" : data?.success_rate_30d >= 70 ? "text-amber-400" : "text-red-400"}`}>
              {data?.success_rate_30d || 0}%
            </p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Sauvegardes 30j</p>
            <p className="text-3xl font-bold text-indigo-400">{data?.total_backups_30d || 0}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Erreurs actives</p>
            <p className={`text-3xl font-bold ${stats.error > 0 ? "text-red-400" : "text-emerald-400"}`}>{stats.error || 0}</p>
          </div>
        </div>

        {/* Trend chart (7 days) */}
        {trends.length > 0 && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5" data-testid="trend-chart">
            <h3 className="text-sm text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <TrendingUp size={14} /> Tendance des 7 derniers jours
            </h3>
            <div className="flex items-end gap-3 h-24">
              {trends.map((t, i) => {
                const height = maxTrend > 0 ? Math.max((t.total_backups / maxTrend) * 100, 4) : 4;
                const rateColor = t.success_rate === null ? "bg-slate-700" : t.success_rate >= 90 ? "bg-emerald-500" : t.success_rate >= 70 ? "bg-amber-500" : "bg-red-500";
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                    <div className={`w-full ${rateColor} rounded-t transition-all opacity-70 hover:opacity-100`} style={{ height: `${height}%` }} />
                    <span className="text-[10px] text-slate-600">{t.date}</span>
                    <div className="hidden group-hover:block absolute bottom-full mb-1 bg-slate-700 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                      {t.total_backups} sauvegardes{t.success_rate !== null ? ` | ${t.success_rate}% OK` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-600">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500" /> &ge;90%</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-500" /> 70-89%</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500" /> &lt;70%</span>
            </div>
          </div>
        )}

        {/* Server list */}
        <div data-testid="server-list">
          <h3 className="text-sm text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Activity size={14} /> Detail par serveur
          </h3>
          {sortedServers.length === 0 ? (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-12 text-center">
              <Shield className="mx-auto h-12 w-12 text-slate-700 mb-3" />
              <p className="text-slate-500">Aucun serveur configure</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedServers.map((server) => {
                const st = statusConfig[server.last_backup_status] || statusConfig.unknown;
                const StIcon = st.icon;
                const isExpanded = expandedServer === server.id;
                const agentLabel = server.agent_type === "iperius" ? "Iperius" : "Veeam";

                return (
                  <div key={server.id} className={`bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden transition-all ${server.last_backup_status === "error" ? "border-l-2 border-l-red-500" : ""}`}>
                    {/* Main row */}
                    <button
                      onClick={() => setExpandedServer(isExpanded ? null : server.id)}
                      className="w-full flex items-center gap-4 p-4 hover:bg-slate-700/30 transition-colors text-left"
                      data-testid={`server-row-${server.hostname}`}
                    >
                      <div className={`p-2 rounded-lg ${st.bg}`}>
                        <StIcon className={`${st.color} ${server.last_backup_status === "running" ? "animate-spin" : ""}`} size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-mono text-sm font-medium text-white truncate">{server.hostname}</h4>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${server.agent_type === "iperius" ? "bg-orange-500/20 text-orange-400" : "bg-green-500/20 text-green-400"}`}>
                            {agentLabel}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate">{server.last_backup_message || st.label}</p>
                      </div>
                      <div className="hidden md:block text-right">
                        <p className="text-xs text-slate-500">Derniere sauvegarde</p>
                        <p className="text-sm text-slate-300">
                          {server.last_backup_time
                            ? formatDistanceToNow(new Date(server.last_backup_time), { addSuffix: true, locale: fr })
                            : "Jamais"}
                        </p>
                      </div>
                      <div className="hidden md:block text-right">
                        <p className="text-xs text-slate-500">Taille</p>
                        <p className="text-sm text-slate-300 font-mono">{formatBytes(server.last_backup_size)}</p>
                      </div>
                      {isExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && server.recent_history && server.recent_history.length > 0 && (
                      <div className="border-t border-slate-700/50 p-4 bg-slate-900/30">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Historique recent</p>
                        <table className="w-full">
                          <thead>
                            <tr className="text-[10px] text-slate-600 uppercase">
                              <th className="text-left pb-2">Date</th>
                              <th className="text-left pb-2">Statut</th>
                              <th className="text-left pb-2">Job</th>
                              <th className="text-left pb-2">VM</th>
                              <th className="text-left pb-2">Taille</th>
                              <th className="text-left pb-2">Duree</th>
                            </tr>
                          </thead>
                          <tbody>
                            {server.recent_history.map((h, idx) => {
                              const hSt = statusConfig[h.status] || statusConfig.unknown;
                              const HIcon = hSt.icon;
                              return (
                                <tr key={idx} className="text-xs border-t border-slate-800/50">
                                  <td className="py-1.5 text-slate-400 font-mono">
                                    {h.backup_time ? new Date(h.backup_time).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-"}
                                  </td>
                                  <td className="py-1.5">
                                    <span className={`inline-flex items-center gap-1 ${hSt.color}`}>
                                      <HIcon size={10} /> {hSt.label}
                                    </span>
                                  </td>
                                  <td className="py-1.5 text-slate-400 font-mono">{h.job_name || "-"}</td>
                                  <td className="py-1.5 text-slate-300 font-mono">{h.vm_name || "-"}</td>
                                  <td className="py-1.5 text-slate-400 font-mono">{formatBytes(h.backup_size)}</td>
                                  <td className="py-1.5 text-slate-400 font-mono">{formatDuration(h.duration)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Error ticker at bottom */}
      {stats.error > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-red-500/10 border-t border-red-500/30 py-2 px-4 z-50">
          <div className="flex items-center gap-4 overflow-hidden">
            <XCircle className="text-red-500 shrink-0" size={16} />
            <div className="whitespace-nowrap overflow-hidden">
              {sortedServers
                .filter((s) => s.last_backup_status === "error")
                .map((s) => (
                  <span key={s.id} className="mx-4 text-sm text-red-400">
                    {s.hostname}: {s.last_backup_message || "Erreur de sauvegarde"}
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringByCode;
